/**
 * KeyValueGrid - A minimal, luxury-styled 2-column grid for key-value pairs
 * Used in admin panels to display order details cleanly
 */
import React from 'react';
import { cn } from '@/lib/utils';

/**
 * KeyValueGrid component
 * @param {Array<{label: string, value: string|ReactNode}>} items - Array of key-value pairs
 * @param {string} className - Additional CSS classes
 * @param {number} columns - Number of columns (1 or 2, default: 2)
 */
export function KeyValueGrid({ items = [], className, columns = 2 }) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div 
      className={cn(
        "grid gap-3",
        columns === 2 ? "md:grid-cols-2" : "grid-cols-1",
        className
      )}
      data-testid="key-value-grid"
    >
      {items.map((item, index) => (
        <KeyValueItem 
          key={`${item.label}-${index}`} 
          label={item.label} 
          value={item.value}
          highlight={item.highlight}
        />
      ))}
    </div>
  );
}

/**
 * Single key-value item
 */
export function KeyValueItem({ label, value, highlight = false }) {
  const isEmpty = value === '—' || value === '' || value === null || value === undefined;
  
  return (
    <div 
      className={cn(
        "p-3 rounded-lg",
        highlight 
          ? "bg-[#C6A14A]/10 border border-[#C6A14A]/20" 
          : "bg-[#050816]/30"
      )}
      data-testid={`kv-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <p className="text-xs text-[#E8E4DE]/50 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={cn(
        "text-sm",
        isEmpty ? "text-[#E8E4DE]/40 italic" : "text-white"
      )}>
        {isEmpty ? '—' : value}
      </p>
    </div>
  );
}

/**
 * KeyValueRow - Horizontal key-value display
 */
export function KeyValueRow({ label, value, className }) {
  const isEmpty = value === '—' || value === '' || value === null || value === undefined;
  
  return (
    <div className={cn("flex justify-between items-center py-2", className)}>
      <span className="text-sm text-[#E8E4DE]/60">{label}</span>
      <span className={cn(
        "text-sm font-medium",
        isEmpty ? "text-[#E8E4DE]/40" : "text-white"
      )}>
        {isEmpty ? '—' : value}
      </span>
    </div>
  );
}

export default KeyValueGrid;
